import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Demandeur, DemandeurCreate } from '../../../types/demandeur';
import { demandeursAPI } from '../../../services/api';
import { useDemandeursList, useDeletedDemandeurs, useInvalidateDemandeurs } from '../../../hooks/queries/useDemandeurs';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import PermissionButton from '../../../components/PermissionButton';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const CONFIG_COLOR = '#1976D2';

const DEMANDEUR_FILTER_SCHEMA = {
  code:   { type: 'text', param: 'code' },
  nom:    { type: 'text', param: 'nom' },
  prenom: { type: 'text', param: 'prenom' },
  email:  { type: 'text', param: 'email' },
  phone:  { type: 'text', param: 'phone' },
  mobile: { type: 'text', param: 'mobile' },
} as const;

const DemandeurManagement: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingDemandeur, setEditingDemandeur] = useState<Demandeur | null>(null);
  const [currentDemandeur, setCurrentDemandeur] = useState<Partial<Demandeur>>({
    code: '',
    nom: '',
    prenom: '',
    email: '',
    phone: '',
    mobile: ''
  });
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    DEMANDEUR_FILTER_SCHEMA
  );

  // Cached list + deleted list; mutations invalidate to refetch.
  const listParams = useMemo(() => {
    const extra = toQueryParams();
    return { extra: Object.keys(extra).length > 0 ? extra : undefined };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const listQuery = useDemandeursList(listParams);
  const deletedQuery = useDeletedDemandeurs(tabValue === 1);
  const invalidate = useInvalidateDemandeurs();
  const demandeurs = listQuery.data?.items ?? [];
  const deletedDemandeurs = deletedQuery.data?.items ?? [];
  const loading = tabValue === 0 ? listQuery.isInitialLoading : deletedQuery.isInitialLoading;

  const handleRestore = async (demandeur: Demandeur) => {
    try {
      await demandeursAPI.restore(demandeur.id);
      showSuccess('Demandeur restauré avec succès');
      invalidate();
    } catch (error) {
      showError('Erreur lors de la restauration du demandeur');
      console.error('Error restoring demandeur:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpen = (demandeur?: Demandeur) => {
    if (demandeur) {
      setEditingDemandeur(demandeur);
      setCurrentDemandeur(demandeur);
      setEditMode(true);
    } else {
      setEditingDemandeur(null);
      setCurrentDemandeur({
        code: '',
        nom: '',
        prenom: '',
        email: '',
        phone: '',
        mobile: ''
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingDemandeur(null);
    setCurrentDemandeur({
      code: '',
      nom: '',
      prenom: '',
      email: '',
      phone: '',
      mobile: ''
    });
    setEditMode(false);
  };

  const handleSave = async () => {
    if (!currentDemandeur.code?.trim() || !currentDemandeur.nom?.trim() || !currentDemandeur.prenom?.trim()) {
      showError('Le code, le nom et le prénom sont obligatoires');
      return;
    }

    try {
      if (editMode && editingDemandeur) {
        await demandeursAPI.update(editingDemandeur.id, currentDemandeur as Demandeur);
        showSuccess('Demandeur modifié avec succès');
      } else {
        const createData: DemandeurCreate = {
          code: currentDemandeur.code!,
          nom: currentDemandeur.nom!,
          prenom: currentDemandeur.prenom!,
          email: currentDemandeur.email,
          phone: currentDemandeur.phone,
          mobile: currentDemandeur.mobile,
          is_active: true
        };
        await demandeursAPI.create(createData);
        showSuccess('Demandeur créé avec succès');
      }
      invalidate();
      handleClose();
    } catch (err: any) {
      console.error('Demandeur creation/update error:', err.response?.data);
      let errorMessage = 'Erreur lors de la sauvegarde';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map((error: any) => 
            `${error.loc ? error.loc.join('.') : 'Field'}: ${error.msg}`
          ).join('\n');
        } else {
          errorMessage = err.response.data.detail;
        }
      }
      showError(errorMessage);
    }
  };

  const handleDelete = async (demandeur: Demandeur) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer le demandeur "${demandeur.nom} ${demandeur.prenom}" ?`,
      async () => {
        try {
          await demandeursAPI.delete(demandeur.id);
          showSuccess('Demandeur supprimé avec succès');
          invalidate();
        } catch (error) {
          showError('Erreur lors de la suppression du demandeur');
          console.error('Error deleting demandeur:', error);
        }
      }
    );
  };


  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5">Gestion des Demandeurs</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
            <PermissionButton
              moduleName="Config"
              operation="add"
              miniModuleName="Demandeurs"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
            >
              Nouveau Demandeur
            </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Demandeurs Actifs" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
      <Box sx={getModuleTableTheme('configuration')}>
        <TableContainer component={Paper}>
          <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Code
                  <ColumnFilter type="text" value={filters.code} onChange={(v) => update('code', v)} label="code" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom
                  <ColumnFilter type="text" value={filters.nom} onChange={(v) => update('nom', v)} label="nom" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Prénom
                  <ColumnFilter type="text" value={filters.prenom} onChange={(v) => update('prenom', v)} label="prénom" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Email
                  <ColumnFilter type="text" value={filters.email} onChange={(v) => update('email', v)} label="email" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Téléphone
                  <ColumnFilter type="text" value={filters.phone} onChange={(v) => update('phone', v)} label="téléphone" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Mobile
                  <ColumnFilter type="text" value={filters.mobile} onChange={(v) => update('mobile', v)} label="mobile" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {demandeurs.map((demandeur) => (
              <TableRow key={demandeur.id}>
                <TableCell>{demandeur.code}</TableCell>
                <TableCell>{demandeur.nom}</TableCell>
                <TableCell>{demandeur.prenom}</TableCell>
                <TableCell>{demandeur.email}</TableCell>
                <TableCell>{demandeur.phone}</TableCell>
                <TableCell>{demandeur.mobile}</TableCell>
                <TableCell align="right">
                  <PermissionButton
                    moduleName="Config"
                    operation="edit"
                    miniModuleName="Demandeurs"
                    component="IconButton"
                    onClick={() => handleOpen(demandeur)}
                    color="primary"
                  >
                    <EditIcon />
                  </PermissionButton>
                  <PermissionButton
                    moduleName="Config"
                    operation="delete"
                    miniModuleName="Demandeurs"
                    component="IconButton"
                    onClick={() => handleDelete(demandeur)}
                    color="error"
                  >
                    <DeleteIcon />
                  </PermissionButton>
                </TableCell>
              </TableRow>
            ))}
            {demandeurs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Aucun demandeur trouvé
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          </Table>
        </TableContainer>
      </Box>
      )}

      {tabValue === 1 && (
      <Box sx={getModuleTableTheme('configuration')}>
        <TableContainer component={Paper}>
          <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Prénom</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Date de Suppression</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deletedDemandeurs.map((demandeur) => (
              <TableRow key={demandeur.id}>
                <TableCell>{demandeur.code}</TableCell>
                <TableCell>{demandeur.nom}</TableCell>
                <TableCell>{demandeur.prenom}</TableCell>
                <TableCell>{demandeur.email}</TableCell>
                <TableCell>{demandeur.deleted_at ? new Date(demandeur.deleted_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => {
                      showConfirm(
                        `Êtes-vous sûr de vouloir restaurer le demandeur "${demandeur.nom} ${demandeur.prenom}" ?`,
                        () => handleRestore(demandeur),
                        {
                          title: 'Confirmer la restauration',
                          confirmText: 'Restaurer',
                          cancelText: 'Annuler'
                        }
                      );
                    }}
                    color="primary"
                    title="Restaurer"
                  >
                    <RestoreIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {deletedDemandeurs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Aucun demandeur supprimé
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          </Table>
        </TableContainer>
      </Box>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Modifier le Demandeur' : 'Nouveau Demandeur'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                label="Code"
                value={currentDemandeur.code || ''}
                onChange={(e) => setCurrentDemandeur({ ...currentDemandeur, code: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom"
                value={currentDemandeur.nom || ''}
                onChange={(e) => setCurrentDemandeur({ ...currentDemandeur, nom: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Prénom"
                value={currentDemandeur.prenom || ''}
                onChange={(e) => setCurrentDemandeur({ ...currentDemandeur, prenom: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                label="Email"
                type="email"
                value={currentDemandeur.email || ''}
                onChange={(e) => setCurrentDemandeur({ ...currentDemandeur, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Téléphone"
                value={currentDemandeur.phone || ''}
                onChange={(e) => setCurrentDemandeur({ ...currentDemandeur, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Mobile"
                value={currentDemandeur.mobile || ''}
                onChange={(e) => setCurrentDemandeur({ ...currentDemandeur, mobile: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default DemandeurManagement;